// THE VEGETATION ATLASES AND THE TREE TILE INDEX IN THE CLIENT WORKER POOL
// (R8c3; client/src/pool.js, ops of poolwork.js OPS).
//
// ATLASES (stateful, affinity 'veg'): the game's tree species / bush / tuft /
// flower atlases are painted HERE, on an OffscreenCanvas, by the vegpaint.js
// painters (the very ones the dashboard's canvases run); the canvas stays in
// this worker under its atlas id, so the owner's sprite composite and the
// season page's readback happen here too and only bytes go to the main thread
// (vegmat.js poolAtlas / atlasSprite / setSpritePage: put into a main canvas,
// uploaded as today's CanvasTexture — no paint, no readback there). The canvas is created bare and its
// context by the painter (getContext('2d'), no willReadFrequently — the owner
// ruled the pixels stay today's, and a CPU-backed context paints others).
//   veg.paint  {id, kind, args, sprites: [{cell, bm}], drop}
//              a new canvas `id` painted (kind: species {form, color, S,
//              forms} | bush {color, bloom, S} | tuft {S, nvar} | flower {}),
//              then each kept owner sprite composited (a repaint after the
//              worker was lost) -> {w, h, px, lum} (px: the canvas's RGBA
//              bytes, top row first, straight alpha — getImageData; lum: a
//              tuft atlas's per-column luminance, else null)
//   veg.sprite {id, cell, bm, drop}
//              the owner's sprite (an ImageBitmap, transferred, closed here)
//              composited into canvas `id`: cell 'tree' = every species cell
//              (vegpaint.js treeSpriteAtlas), cell v = bush column v
//              (bushSpriteCell) -> {w, h, px}
//   veg.page   {id, a0, drop}
//              canvas `id`'s season page (vegpaint.js seasonPage, alpha cut
//              a0) -> {w, h, page}
// `drop`: ids of atlases the main thread disposed (their canvases go). An id
// this worker does not hold throws `veg: no atlas <id>` — the worker was
// replaced (the pool starts it empty): the main thread paints it again from
// the inputs it keeps.
//
// TREE TILES (stateless, any worker):
//   trees.tile {ab, i, j, n, start}
//              one CPK1 crown pack (transferred) decoded (crownpack.js) and
//              indexed for trees.js Forest: xyz (x, NaN, z), key (hash >>>
//              12), h, r, appear, ids (the tile's slots start..start+n) and
//              grid: the tile's trunk-collision cells (COLL_M) as CSR — {x0,
//              z0, w, h, off, items}: cell (cx, cz)'s slots are
//              items[off[c] .. off[c + 1]], c = (cz - z0)·w + (cx - x0), in
//              record order
//
// BOOT TEXTURES (R8c5; stateless, any worker):
//   tex.paint  {kind, args}
//              texpaint.js TEX_PAINT[kind] (detail {mean} | bore {}) painted
//              on a new S x S OffscreenCanvas (no willReadFrequently, as the
//              atlases) -> {w, h, px} (getImageData, as veg.paint); the
//              canvas goes with the job (roads3d.js poolTexture puts the
//              bytes into its main canvas)
//
// Pure + DOM-free (the worker bundle: poolwork.js imports this module).
import {
  paintSpeciesCanvas, paintBushCanvas, paintTuftCanvas, paintFlowerCanvas, treeSpriteAtlas, bushSpriteCell, seasonPage,
} from './vegpaint.js';
import { decodeCrowns } from './crownpack.js';
import { TEX_PAINT } from './texpaint.js';

// ---------------------------------------------------------------- atlases
const _atlas = new Map();       // this worker's canvases: id -> OffscreenCanvas

const PAINT = {
  species: (cv, a) => paintSpeciesCanvas(cv, a.form, a.color, a.S, a.forms || {}),
  bush: (cv, a) => paintBushCanvas(cv, a.color, a.bloom, a.S),
  tuft: (cv, a) => paintTuftCanvas(cv, a.S, a.nvar),
  flower: (cv) => paintFlowerCanvas(cv),
};

// per tuft column the alpha-weighted mean linear luminance of the painted
// blades (grass.js's shader divides the blade shading by it: mean 1); 1 for
// an empty column or unreadable bytes
export function tuftLum(d, w, h, nvar) {
  const lum = new Array(nvar).fill(1);
  if (!d || d.length !== w * h * 4) return lum;
  const lin = (v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const S = w / nvar;
  for (let v = 0; v < nvar; v++) {
    let sy = 0, sa = 0;
    for (let y = 0; y < h; y++) for (let x = v * S; x < (v + 1) * S; x++) {
      const o = (y * w + x) * 4, a = d[o + 3] / 255;
      sy += a * (0.2126 * lin(d[o]) + 0.7152 * lin(d[o + 1]) + 0.0722 * lin(d[o + 2]));
      sa += a;
    }
    if (sa > 0 && sy > 0) lum[v] = sy / sa;
  }
  return lum;
}

const drop = (ids) => { for (const id of ids || []) _atlas.delete(id); };
function atlasOf(id) {
  const cv = _atlas.get(id);
  if (!cv) throw new Error(`veg: no atlas ${id}`);
  return cv;
}
const pixels = (cv) => cv.getContext('2d').getImageData(0, 0, cv.width, cv.height).data;
function composite(cv, s) {
  try {
    if (s.cell === 'tree') treeSpriteAtlas(cv, s.bm);
    else bushSpriteCell(cv, s.bm, s.cell);
  } finally {
    if (s.bm && typeof s.bm.close === 'function') s.bm.close();
  }
}
const bytes = (cv, extra = {}) => {
  const px = pixels(cv);
  return { result: { w: cv.width, h: cv.height, px, ...extra }, transfer: [px.buffer] };
};

function vegPaint(a) {
  drop(a.drop);
  const f = Object.prototype.hasOwnProperty.call(PAINT, a.kind) ? PAINT[a.kind] : null;
  const sprites = a.sprites || [];
  if (!f) {
    for (const s of sprites) if (s.bm && s.bm.close) s.bm.close();
    throw new Error(`veg: no atlas kind ${a.kind}`);
  }
  const cv = new OffscreenCanvas(1, 1);
  f(cv, a.args || {});
  for (const s of sprites) composite(cv, s);
  _atlas.set(a.id, cv);
  const out = bytes(cv);
  out.result.lum = a.kind === 'tuft' ? tuftLum(out.result.px, cv.width, cv.height, a.args.nvar) : null;
  return out;
}

function vegSprite(a) {
  drop(a.drop);
  let cv;
  try { cv = atlasOf(a.id); } catch (e) { if (a.bm && a.bm.close) a.bm.close(); throw e; }
  composite(cv, a);
  return bytes(cv);
}

function vegPage(a) {
  drop(a.drop);
  const cv = atlasOf(a.id);
  const page = seasonPage(pixels(cv), cv.width, cv.height, a.a0);
  return { result: { w: cv.width, h: cv.height, page }, transfer: [page.buffer] };
}

// ------------------------------------------------------------- tree tiles
export const COLL_M = 12;       // trees.js Forest: the trunk-collision cell (m)

function treesTile({ ab, i, j, n, start }) {
  const d = decodeCrowns(ab);
  if (d.kind !== 0 || d.i !== i || d.j !== j || d.n !== n) throw new Error(`pack holds ${d.kind} ${d.i}_${d.j} n ${d.n}`);
  const xyz = new Float32Array(n * 3), key = new Uint32Array(n), ids = new Int32Array(n);
  const cx = new Int32Array(n), cz = new Int32Array(n);
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (let k = 0; k < n; k++) {
    xyz[k * 3] = d.x[k];
    xyz[k * 3 + 1] = NaN;
    xyz[k * 3 + 2] = d.z[k];
    key[k] = d.hash[k] >>> 12;
    ids[k] = start + k;
    const a = Math.floor(xyz[k * 3] / COLL_M), b = Math.floor(xyz[k * 3 + 2] / COLL_M);
    cx[k] = a; cz[k] = b;
    if (a < x0) x0 = a;
    if (a > x1) x1 = a;
    if (b < z0) z0 = b;
    if (b > z1) z1 = b;
  }
  const w = n ? x1 - x0 + 1 : 0, h = n ? z1 - z0 + 1 : 0;
  const off = new Uint32Array(w * h + 1), items = new Int32Array(n);
  for (let k = 0; k < n; k++) off[(cz[k] - z0) * w + (cx[k] - x0) + 1]++;
  for (let c = 0; c < w * h; c++) off[c + 1] += off[c];
  const at = off.slice(0, w * h);
  for (let k = 0; k < n; k++) items[at[(cz[k] - z0) * w + (cx[k] - x0)]++] = start + k;
  const grid = { x0: n ? x0 : 0, z0: n ? z0 : 0, w, h, off, items };
  return {
    result: { xyz, key, h: d.h, r: d.r, appear: d.appear, ids, grid },
    transfer: [xyz.buffer, key.buffer, d.h.buffer, d.r.buffer, d.appear.buffer, ids.buffer, off.buffer, items.buffer],
  };
}

// ------------------------------------------------------------ boot textures
function texPaint({ kind, args }) {
  const T = Object.prototype.hasOwnProperty.call(TEX_PAINT, kind) ? TEX_PAINT[kind] : null;
  if (!T) throw new Error(`tex: no texture kind ${kind}`);
  const cv = new OffscreenCanvas(T.S, T.S);
  T.paint(cv.getContext('2d'), T.S, args || {});
  return bytes(cv);
}

// poolwork.js OPS (spread in)
export const VEG_OPS = {
  'veg.paint': vegPaint,
  'veg.sprite': vegSprite,
  'veg.page': vegPage,
  'trees.tile': treesTile,
  'tex.paint': texPaint,   // R8c5: the boot procedural textures (structs2 detail, roads3d bore)
};
